import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';

import type { CompanyFreeTextField } from '@/api/v4/organization.api';

type CompanyFreeTextFieldForm = Nullable<CompanyFreeTextField> & {
  id: string;
  synced: boolean;
};

import {
  companyFreeTextFields,
  companyFreeTextFieldsEditModeState,
} from './free-text-fields.state';
import { useCompanyFreeTextFields } from './use-company-free-text-fields.query';

const MAX_FREE_TEXT_FIELDS_COUNT = 5;

export const useFreeTextFields = () => {
  const [fields, setFields] = useAtom(companyFreeTextFields);
  const [editModeFieldId, setEditModeFieldId] = useAtom(
    companyFreeTextFieldsEditModeState,
  );
  const { data, isLoading, refetch } = useCompanyFreeTextFields();
  const refresh = useCallback(async () => {
    setFields([]);
    await refetch();
  }, [refetch, setFields]);
  const isEditing = !!editModeFieldId;

  useEffect(() => {
    if (data && data.length > 0) {
      setFields(data.map(field => ({ ...field, synced: true })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const add = () => {
    if (isEditing) {
      return;
    }

    const tempId = `${fields.length}`;
    setEditModeFieldId(tempId);

    setFields((fields: CompanyFreeTextFieldForm[]) => [
      ...fields,
      { id: `${tempId}`, title: '', content: '', synced: false },
    ]);
  };

  const edit = (id: string) => setEditModeFieldId(id);

  const deleteItem = (id: string) => {
    setEditModeFieldId(null);
    setFields((fields: CompanyFreeTextFieldForm[]) =>
      fields.filter((field: CompanyFreeTextFieldForm) => field.id !== id),
    );
  };

  const onSync = (tempId: string, persistedValue: CompanyFreeTextField) => {
    setFields((fields: CompanyFreeTextFieldForm[]) =>
      fields.map((field: CompanyFreeTextFieldForm) => {
        if (field.id === tempId) {
          return {
            ...persistedValue,
            synced: true,
          };
        }

        return field;
      }),
    );
  };

  const canAddExtraField = fields.length < MAX_FREE_TEXT_FIELDS_COUNT;

  const isFreeTextFieldAdded = Boolean(fields?.length);

  return {
    fields,
    isLoading,
    add,
    edit,
    onSync,
    deleteItem,
    refresh,
    canAddExtraField,
    isFreeTextFieldAdded,
  };
};
